import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";
import { resolveIcon } from "@/lib/iconResolver";

export interface IndustriesContent {
  eyebrow: string;
  headline: string;
  industries: { name: string; icon: string }[];
}

export const industriesDefaults: IndustriesContent = {
  eyebrow: "Industry Experience",
  headline: "Growth systems built for complex verticals.",
  industries: [
    { name: "E-commerce", icon: "ShoppingBag" },
    { name: "SaaS", icon: "Laptop" },
    { name: "Beauty & Wellness", icon: "Sparkles" },
    { name: "Health", icon: "Heart" },
    { name: "Home & Living", icon: "Home" },
    { name: "Education", icon: "GraduationCap" },
    { name: "Finance", icon: "TrendingUp" },
    { name: "Professional Services", icon: "Briefcase" },
  ],
};

interface IndustriesProps {
  content?: IndustriesContent;
}

const Industries = ({ content: c = industriesDefaults }: IndustriesProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border">
      <div className="container-wide">
        <motion.div 
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            {c.headline}
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.industries.map((industry) => {
            const Icon = resolveIcon(industry.icon);
            return (
              <motion.div
                key={industry.name}
                variants={itemVariant}
                className="group bg-background p-5 md:p-6 lg:p-10 transition-colors duration-150 hover:bg-muted/20"
              >
                <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center mb-5 transition-colors group-hover:bg-muted">
                  <Icon className="h-5 w-5 text-foreground/60" strokeWidth={1.5} />
                </div>
                <span className="text-sm text-foreground/80 font-medium">{industry.name}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Industries;
